import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, type Dispatch, type SetStateAction } from "react";
import { cn } from "../../../lib/utils";

export type PromotionalBannersLayout = "full" | "aside";

interface PromotionalBannersProps {
  banners?: BannerItem[];
  currentBanner: number;
  setCurrentBanner: Dispatch<SetStateAction<number>>;
  /** full: standalone section (single row). aside: promo column beside hero on large screens. */
  layout?: PromotionalBannersLayout;
  /** When true, fills parent card width (Bizcover shop detail). */
  embedded?: boolean;
}

export type BannerItem = {
  id?: string | number;
  imageUrl?: string;
  image?: string;
  title?: string;
  headline?: string;
  order?: number;
};

const getBannerImageSrc = (banner: BannerItem): string =>
  banner.imageUrl || banner.image || "";

const getBannerOrder = (banner: BannerItem): number => {
  const raw = banner.order;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};

/**
 * Public storefront shows both dedicated promotional banners and extra storefront
 * banners (order ≥ 1) in one carousel. De-duplicates by image URL so the same
 * asset is not shown twice.
 */
export function mergePromotionalAndStorefrontSliderBanners(
  promotional: BannerItem[],
  storefrontSlider: BannerItem[],
): BannerItem[] {
  const sortedPromo = [...promotional].sort((a, b) => getBannerOrder(a) - getBannerOrder(b));
  const sortedSlider = [...storefrontSlider].sort((a, b) => getBannerOrder(a) - getBannerOrder(b));
  const seen = new Set<string>();
  const out: BannerItem[] = [];
  const pushUnique = (banner: BannerItem) => {
    const src = getBannerImageSrc(banner).trim();
    const key =
      src.length > 0 ? `url:${src}` : banner.id != null ? `id:${String(banner.id)}` : `idx:${out.length}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(banner);
  };
  sortedPromo.forEach(pushUnique);
  sortedSlider.forEach(pushUnique);
  return out;
}

export const PromotionalBanners = ({
  banners,
  currentBanner,
  setCurrentBanner,
  layout = "full",
  embedded = false,
}: PromotionalBannersProps) => {
  const list = banners ?? [];
  const hasMultipleItems = list.length > 1;

  useEffect(() => {
    if (!hasMultipleItems) return;

    const intervalId = window.setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % list.length);
    }, 4000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [hasMultipleItems, list.length, setCurrentBanner]);

  if (list.length === 0) {
    return null;
  }

  const isAside = layout === "aside";
  const safeCurrent = Math.min(currentBanner, list.length - 1);

  const bannerSlide = (banner: BannerItem, index: number) => {
    const src = getBannerImageSrc(banner);
    if (!src) {
      return (
        <div
          className={cn(
            "relative w-full bg-gradient-to-br from-[#FF6A00]/15 to-slate-100",
            isAside
              ? "aspect-[3/2]"
              : "aspect-[5/3] sm:aspect-[2.4/1] md:aspect-[2.75/1] lg:aspect-[3/1]",
          )}
        />
      );
    }
    return (
      <div
        className={cn(
          "relative w-full",
          /* Aside is 4/12 grid cols; 3:2 height matches hero 3:1 on 8/12 cols */
          isAside
            ? "aspect-[3/2]"
            : "aspect-[5/3] sm:aspect-[2.4/1] md:aspect-[2.75/1] lg:aspect-[3/1]",
        )}
      >
        <img
          src={src}
          alt={banner.title || banner.headline || `Promotional banner ${index + 1}`}
          className="absolute inset-0 h-full w-full object-cover object-center"
          loading={index === 0 ? "eager" : "lazy"}
          decoding="async"
          sizes={
            isAside ? "(max-width: 1023px) 100vw, 420px" : "(max-width: 1536px) 100vw, 1536px"
          }
        />
      </div>
    );
  };

  const carouselCard = (
    <div className="overflow-hidden rounded-2xl border border-gray-200/90 bg-slate-100 shadow-[0_20px_50px_-18px_rgba(0,0,0,0.15)] ring-1 ring-black/[0.05]">
      {hasMultipleItems ? (
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{
            width: `${list.length * 100}%`,
            transform: `translateX(-${(safeCurrent * 100) / list.length}%)`,
          }}
        >
          {list.map((banner, index: number) => (
            <div
              key={banner.id || index}
              style={{ width: `${100 / list.length}%` }}
              className="shrink-0"
            >
              {bannerSlide(banner, index)}
            </div>
          ))}
        </div>
      ) : (
        bannerSlide(list[0], 0)
      )}
    </div>
  );

  const controls = hasMultipleItems && (
    <>
      <button
        type="button"
        onClick={() =>
          setCurrentBanner((prev) => (prev - 1 + list.length) % list.length)
        }
        className={cn(
          "absolute top-1/2 z-10 flex min-h-[44px] min-w-[44px] -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-lg transition-colors hover:bg-white",
          isAside ? "left-1.5 sm:left-2" : "left-2 sm:left-4",
        )}
        aria-label="Previous media"
      >
        <ChevronLeft className="h-5 w-5 text-[#FF6A00] sm:h-6 sm:w-6" />
      </button>
      <button
        type="button"
        onClick={() => setCurrentBanner((prev) => (prev + 1) % list.length)}
        className={cn(
          "absolute top-1/2 z-10 flex min-h-[44px] min-w-[44px] -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-lg transition-colors hover:bg-white",
          isAside ? "right-1.5 sm:right-2" : "right-2 sm:right-4",
        )}
        aria-label="Next media"
      >
        <ChevronRight className="h-5 w-5 text-[#FF6A00] sm:h-6 sm:w-6" />
      </button>
    </>
  );

  const dots =
    hasMultipleItems && (
      <div className="flex justify-center gap-2 pt-3 sm:pt-4">
        {list.map((_, index: number) => (
          <button
            key={index}
            type="button"
            onClick={() => setCurrentBanner(index)}
            className={cn(
              "min-h-[10px] min-w-[10px] rounded-full transition-all duration-200 sm:min-h-[12px] sm:min-w-[12px]",
              index === safeCurrent
                ? "scale-110 bg-[#FF6A00] ring-2 ring-[#FF6A00]/30"
                : "bg-gray-300 hover:bg-gray-400",
            )}
            aria-label={`Go to media ${index + 1}`}
          />
        ))}
      </div>
    );

  if (isAside) {
    return (
      <div className="flex w-full flex-col" aria-live="polite" aria-roledescription="carousel">
        <div className="relative">
          {carouselCard}
          {controls}
        </div>
        {dots}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "border-b border-gray-100/80",
        embedded ? "bg-white px-4 py-4 sm:px-6 sm:py-6" : "bg-gradient-to-b from-white to-slate-50/60 py-5 sm:py-8",
      )}
      aria-live="polite"
      aria-roledescription="carousel"
    >
      <div className={embedded ? "w-full" : "container mx-auto px-4"}>
        <div className="relative">
          {carouselCard}
          {controls}
          {dots}
        </div>
      </div>
    </div>
  );
};
